import { gsap } from "gsap"
import { module as BaseModule } from "modujs"

export default class extends BaseModule {
  init() {
    this.state = false
    this.scrollThresholdMobile = 10
    this.scrollThresholdDesktop = 20
    this.handleResize = this.handleResize.bind(this)
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this)

    this.toggleBtns = document.querySelectorAll("[data-menu-toggle]")
    this.toggleBtns.forEach((btn) => {
      btn.addEventListener("click", this.toggleMenu.bind(this))
    })

    this.setTimeline()
    this.setAltMenu()
    window.addEventListener("resize", this.handleResize)
    document.addEventListener("click", this.handleDocumentClick)
    document.addEventListener("keydown", this.handleDocumentKeydown)
  }

  setAltMenu() {

    const slogan = document.querySelector("[data-header-slogan]")
    const star = document.querySelector("[data-header-star]")
    const logo = document.querySelector("[data-header-logo]")
    const logoAlt = document.querySelector("[data-header-logo-alt]")
    const menuToggle = document.querySelector("[data-menu-toggle]")

    gsap.set(logoAlt, { autoAlpha: 0 })
    logoAlt.classList.remove("hidden")

    const altTimeline = gsap
      .timeline({ paused: true, onReverseComplete: () => {
        gsap.to(star, {
          rotation: 0,
          duration: 1,
          ease: "power2.out",
        })
      } })
      .to([slogan, logo], {
        autoAlpha: 0,
        overwrite: true,
      }, 0)
      .to([logoAlt], {
        autoAlpha: 1,
        overwrite: true,
      }, 0.5)
      .to(menuToggle, {
       color: "white",
       backgroundColor: "black",
      }, 0.5)


    window.lenis.on("scroll", ({ scroll }) => {
      const threshold = window.innerWidth > 900 ? this.scrollThresholdDesktop : this.scrollThresholdMobile
      const isPastThreshold = scroll > threshold
      if (isPastThreshold) {
        altTimeline.play()
      } else {
        altTimeline.reverse()
      }
    })
  }

  setTimeline() {
    this.isDesktop = window.innerWidth > 768

    this.wrapper = document.querySelector("[data-menu-wrapper]")
    this.separator = document.querySelector("[data-menu-separator]")
    this.infoClones = document.querySelectorAll("[data-menu-info] .clone")
    this.contactLinks = document.querySelectorAll("[data-menu-contact] a")
    this.navLinks = document.querySelectorAll("[data-menu-nav] li")
    this.socialLinks = document.querySelectorAll("[data-menu-social] li")
    this.lottieElement = document.querySelector("[data-module-lottie][data-id='logo_v6']")
    this.menuLineTop = this.el?.querySelector("#line-top")
    this.menuLineMid1 = this.el?.querySelector("#line-mid-1")
    this.menuLineMid2 = this.el?.querySelector("#line-mid-2")
    this.menuLineBottom = this.el?.querySelector("#line-bottom")

    if (this.timeline) {
      this.timeline.kill()
    }

    // Burger icon initial states
    gsap.set([this.menuLineMid1, this.menuLineMid2], { transformOrigin: "50% 50%" })
    gsap.set(this.menuLineMid2, { opacity: 0 })

    // Menu element initial states
    gsap.set(this.infoClones, { yPercent: 110 })
    gsap.set(this.contactLinks, { autoAlpha: 0 })
    gsap.set(this.navLinks, { autoAlpha: 0, y: 8 })
    gsap.set(this.socialLinks, { autoAlpha: 0, y: 8 })

    if (this.isDesktop) {
      gsap.set(this.separator, { scaleY: 0, transformOrigin: "top top" })
    } else {
      gsap.set(this.separator, { scaleX: 0, transformOrigin: "left center" })
    }

    // Base timeline: wrapper open + burger morph + lottie
    this.timeline = gsap
      .timeline({ paused: true })
      .set(this.wrapper, { display: "grid", opacity: 0, width: 0, height: 0 }, 0)
      .to(this.wrapper, {
        opacity: 1,
        width: () => this.getWrapperSize().width,
        height: () => this.getWrapperSize().height,
      }, 0)
      .to([this.menuLineTop, this.menuLineBottom], { opacity: 0, duration: 0.2, ease: "power2.out" }, 0)
      .to(this.menuLineMid1, { rotation: 45, duration: 0.4, ease: "power2.out" }, 0)
      .to(this.menuLineMid2, { opacity: 1, rotation: -45, duration: 0.4, ease: "power2.out" }, 0)
      .to(this.el, { color: "white" }, 0.5)
      .call(() => {
        if (this.lottieElement) {
          const lottieId = this.lottieElement.dataset.id
          if (lottieId) {
            this.lottieElement.dispatchEvent(new CustomEvent("lottie:play", { detail: { id: lottieId } }))
          }
        }
      }, null, 0)

    if (this.isDesktop) {
      // Info clones: mask reveal via translateY, staggered
      this.timeline
        .to(this.infoClones, {
          yPercent: 0,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.1,
        }, 0.65)
        // Contact links: staggered fade in
        .to(this.contactLinks, {
          autoAlpha: 1,
          duration: 0.5,
          ease: "power2.out",
          stagger: 0.1,
        }, 0.9)
        // Vertical separator: scale Y
        .to(this.separator, {
          scaleY: 1,
          duration: 0.8,
          ease: "power2.out",
        }, 1.1)
        // Nav links: staggered fade from below
        .to(this.navLinks, {
          autoAlpha: 1,
          y: 0,
          duration: 0.5,
          ease: "power2.out",
          stagger: 0.09,
        }, 1.3)
        // Social links: staggered fade from below
        .to(this.socialLinks, {
          autoAlpha: 1,
          y: 0,
          duration: 0.5,
          ease: "power2.out",
          stagger: 0.09,
        }, 1.9)
    } else {
      // Mobile: nav first, then separator, then info/contact, then social
      this.timeline
        // Nav links: staggered fade from below
        .to(this.navLinks, {
          autoAlpha: 1,
          y: 0,
          duration: 0.4,
          ease: "power2.out",
          stagger: 0.07,
        }, 0.5)
        // Horizontal separator: scale X
        .to(this.separator, {
          scaleX: 1,
          duration: 0.5,
          ease: "power2.out",
        }, 0.85)
        // Info clones: mask reveal via translateY, staggered
        .to(this.infoClones, {
          yPercent: 0,
          duration: 0.6,
          ease: "power3.out",
          stagger: 0.08,
        }, 0.95)
        // Contact links: staggered fade in from below
        .to(this.contactLinks, {
          autoAlpha: 1,
          y: 0,
          duration: 0.4,
          ease: "power2.out",
          stagger: 0.08,
        }, 1.3)
        // Social links: staggered fade from below
        .to(this.socialLinks, {
          autoAlpha: 1,
          y: 0,
          duration: 0.4,
          ease: "power2.out",
          stagger: 0.07,
        }, 1.5)
    }
  }

  toggleMenu() {
    if (this.state) {
      this.closeMenu()
    } else {
      this.openMenu()
    }
  }

  async openMenu() {
    this.state = true
    document.body.classList.add("has-menu")
    this.el.setAttribute("aria-expanded", true)
    this.timeline.timeScale(1)
    this.timeline.invalidate()
    this.timeline.play()
  }

  async closeMenu() {
    this.state = false
    document.body.classList.remove("has-menu")
    this.el.setAttribute("aria-expanded", false)
    
    // Dispatch custom event to reverse Lottie animation
    if (this.lottieElement) {
      const lottieId = this.lottieElement.dataset.id
      if (lottieId) {
        const event = new CustomEvent("lottie:reverse", {
          detail: { id: lottieId },
        })
        this.lottieElement.dispatchEvent(event)
      }
    }
    
    this.timeline.timeScale(2)
    this.timeline.reverse()
  }

  getWrapperSize() {
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight

    if (viewportWidth > 768) {
      return { width: "586px", height: "344px" }
    }

    return {
      width: `${viewportWidth - 16}px`,
      height: `${viewportHeight - 16}px`,
    }
  }

  handleResize() {
    if (!this.wrapper || !this.timeline) {
      return
    }

    const isDesktop = window.innerWidth > 768

    if (isDesktop !== this.isDesktop) {
      // Breakpoint crossed — rebuild the full timeline
      if (!this.state) {
        this.setTimeline()
      } else {
        // Can't rebuild while menu is open; track the new breakpoint for next time
        this.isDesktop = isDesktop
      }
    } else {
      if (this.state) {
        gsap.set(this.wrapper, this.getWrapperSize())
      }
    }
  }

  handleDocumentClick(event) {
    if (!this.state || !this.wrapper) {
      return
    }

    const target = event.target
    if (!(target instanceof Element)) {
      return
    }

    const clickedToggle = target.closest("[data-menu-toggle]")
    if (clickedToggle) {
      return
    }

    const clickedInsideMenu = this.wrapper.contains(target)
    if (!clickedInsideMenu) {
      this.closeMenu()
    }
  }

  handleDocumentKeydown(event) {
    if (!this.state) {
      return
    }

    if (event.key === "Escape") {
      this.closeMenu()
    }
  }
}
